import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion, useInView } from "framer-motion";
import { cn } from "@/lib/utils";

const TextRotate = forwardRef(
  (
    {
      texts,
      transition = { type: "spring", damping: 30, stiffness: 300 },
      initial = { opacity: 0, y: 10 },
      animate = { opacity: 1, y: 0 },
      exit = { opacity: 0, y: -10 },
      animatePresenceMode = "wait",
      animatePresenceInitial = false,
      rotationInterval = 3000,
      staggerDuration = 0.05,
      staggerFrom = "first",
      loop = true,
      auto = true,
      splitBy = "characters",
      onNext,
      mainClassName,
      splitLevelClassName,
      elementLevelClassName,
      containerClassName,
      ...props
    },
    ref
  ) => {
    const [currentTextIndex, setCurrentTextIndex] = useState(0);
    const localRef = useRef(null);
    const isInView = useInView(localRef, { once: true });

    const splitIntoCharacters = (text) => {
      if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
        return Array.from(
          new Intl.Segmenter("en", { granularity: "grapheme" }).segment(text),
          ({ segment }) => segment
        );
      }
      return Array.from(text);
    };

    const elements = useMemo(() => {
      const currentText = texts[currentTextIndex];
      if (splitBy === "characters") {
        const text = currentText.split(" ");
        return text.map((word, i) => ({
          characters: splitIntoCharacters(word),
          needsSpace: i !== text.length - 1,
        }));
      }
      return splitBy === "words"
        ? currentText.split(" ")
        : splitBy === "lines"
        ? currentText.split("\n")
        : currentText.split(splitBy);
    }, [texts, currentTextIndex, splitBy]);

    const getStaggerDelay = useCallback(
      (index, totalChars) => {
        if (staggerFrom === "first") return index * staggerDuration;
        if (staggerFrom === "last")
          return (totalChars - 1 - index) * staggerDuration;
        if (staggerFrom === "center")
          return Math.abs(Math.floor(totalChars / 2) - index) * staggerDuration;
        if (staggerFrom === "random")
          return (
            Math.abs(Math.floor(Math.random() * totalChars) - index) *
            staggerDuration
          );
        return Math.abs(staggerFrom - index) * staggerDuration;
      },
      [staggerFrom, staggerDuration]
    );

    const handleIndexChange = useCallback(
      (newIndex) => {
        if (newIndex !== currentTextIndex) {
          setCurrentTextIndex(newIndex);
          onNext?.(newIndex);
        }
      },
      [currentTextIndex, onNext]
    );

    const next = useCallback(
      () => handleIndexChange((currentTextIndex + 1) % texts.length),
      [currentTextIndex, texts.length, handleIndexChange]
    );

    const previous = useCallback(
      () =>
        handleIndexChange((currentTextIndex - 1 + texts.length) % texts.length),
      [currentTextIndex, texts.length, handleIndexChange]
    );

    const jumpTo = useCallback(
      (index) =>
        handleIndexChange(Math.max(0, Math.min(index, texts.length - 1))),
      [texts.length, handleIndexChange]
    );

    const reset = useCallback(() => handleIndexChange(0), [handleIndexChange]);

    useImperativeHandle(ref, () => ({ next, previous, jumpTo, reset }), [
      next,
      previous,
      jumpTo,
      reset,
    ]);

    useEffect(() => {
      if (!auto) return;
      const intervalId = setInterval(next, rotationInterval);
      return () => clearInterval(intervalId);
    }, [next, rotationInterval, auto]);

    return (
      <motion.span
        className={cn("flex flex-wrap whitespace-pre-wrap", mainClassName)}
        {...props}
        layout
        transition={transition}
      >
        <span className="sr-only">{texts[currentTextIndex]}</span>
        <AnimatePresence
          mode={animatePresenceMode}
          initial={animatePresenceInitial}
        >
          <motion.div
            key={currentTextIndex}
            className={cn(
              "flex flex-wrap", // Domyślne klasy
              splitBy === "lines" && "flex-col w-full gap-0",
              splitBy === "words" && "gap-1.5",
              containerClassName // Możliwość nadpisania
            )}
            layout
            aria-hidden="true"
          >
            {elements.map((wordObj, wordIndex, array) => {
              const previousCharsCount = array
                .slice(0, wordIndex)
                .reduce((sum, word) => sum + (word.characters?.length || 1), 0);
              return (
                <span
                  key={wordIndex}
                  className={cn("inline-flex", splitLevelClassName)}
                >
                  {(wordObj.characters || [wordObj]).map((char, charIndex) => (
                    <motion.span
                      ref={localRef}
                      initial={initial}
                      animate={isInView ? animate : initial}
                      exit={exit}
                      key={charIndex}
                      transition={{
                        ...transition,
                        delay: getStaggerDelay(
                          previousCharsCount + charIndex,
                          array.reduce(
                            (sum, word) => sum + (word.characters?.length || 1),
                            0
                          )
                        ),
                      }}
                      className={cn("inline-block", elementLevelClassName)}
                    >
                      {char}
                    </motion.span>
                  ))}
                  {wordObj.needsSpace && (
                    <span className="whitespace-pre"> </span>
                  )}
                </span>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </motion.span>
    );
  }
);

TextRotate.displayName = "TextRotate";
export { TextRotate };
